/**
 * Title + description for routes whose React page component is shared across
 * several SEO paths (DirectorHome serves 5 paths, DownloadApp serves 2).
 *
 * Why this exists: the server renders per-route SEO from ROUTE_SEO in
 * server/_core/seo.ts, but a shared component calls useSEO() with one
 * hardcoded title, which overwrites the server's <title>/description on
 * hydration. Crawlers that don't run JS keep the correct SSR head, but Google
 * renders JS — so it saw every one of these routes carrying the shared
 * component's title (e.g. /ai-music-video-generator presenting the homepage
 * title), which reads as duplicate titles across the cluster.
 *
 * Both sides import this map, so the hydrated <head> matches the SSR <head>
 * and the two can't drift. Server: ROUTE_SEO spreads these values. Client:
 * DirectorHome / DownloadApp look up the current path and pass the result to
 * useSEO.
 *
 * Only paths where the shared component would otherwise clobber a distinct
 * server title belong here. "/" is intentionally absent: its SSR title already
 * equals DirectorHome's default, so there is nothing to correct.
 */
import {
  DANCE_DIRECTOR_PATH,
  DANCE_SEO_HEADS,
  getDanceSeoHead,
} from "./danceSeoHead.js";
import { MV_GENERATOR_PATH, getMvGeneratorHead } from "./mvGeneratorHead.js";

export type SharedComponentRouteSeo = {
  title: string;
  description: string;
  /** Set where the page has its own keywords in that language. */
  keywords?: string[];
};

export const SHARED_COMPONENT_ROUTE_SEO: Record<
  string,
  SharedComponentRouteSeo
> = {
  "/ai-music-video-generator": {
    // ≤60 chars so the full promise survives Google's ~600px title cut (the
    // old 66-char title truncated at "into a …"). Brand suffix dropped on
    // purpose: Google renders the site name above the title anyway. English
    // here; Japanese, Korean and Portuguese come from
    // shared/mvGeneratorHead.ts (see getSharedComponentRouteSeo below).
    title: "AI Music Video Generator — Turn Any Song into a Video",
    description:
      "Solmi's AI music video generator turns any song, Suno track, or MP3 into a finished, lip-synced music video. Beat-synced visuals, auto lyrics, free to start.",
  },
  "/free-ai-music-video-generator": {
    // Oct 11 2026 (Search Console, Sep 11 - Oct 8): #6.2 for "free ai music
    // video generator" (4,005 impressions) and #7.5 for "ai music video
    // generator free" (1,712). The old title said "free" twice ("— Try It
    // Free, No Card | Solmi"); this one keeps the exact phrase up front and
    // spends the rest on what the page offers. The description opens with
    // the reversed phrasing and states the free tier as the code grants it
    // (shared/freeMusicVideoSeo.ts). No "| Solmi": ≤60 characters, and
    // Google shows the site name above the title anyway.
    title: "Free AI Music Video Generator — Lip-Synced, No Credit Card",
    description:
      "Try Solmi's AI music video generator free: 100 credits on signup, no credit card, for a lip-synced 20-second 480p preview. Paid plans render the full song.",
  },
  "/ai-rap-video-generator": {
    // Oct 18 2026 (Search Console, Sep 7 - Oct 17): 30 rap-video queries,
    // about 6,300 impressions, landing on the homepage at #4-8 with nothing
    // rap-specific to read; "ai rap video generator" alone 2,626 impressions
    // (US, UK, Canada, Australia). The title leads with the exact query and
    // says what the page makes; ≤60 characters, no "| Solmi" (Google shows
    // the site name above the title). Body: shared/rapVideoSeo.ts.
    title: "AI Rap Video Generator — Rap Song to Lip-Synced Video",
    description:
      "Upload your rap track (MP3, WAV, or Suno download): Solmi's AI rap video generator storyboards it, casts you from a photo, and lip-syncs every bar. Free start.",
  },
  "/suno-to-video": {
    // Search Console (28 days to Oct 6 2026): 7,007 impressions at #7-9 for
    // "suno video download", "suno downloader mp4", "suno download mp3",
    // "suno to mp4", 188 clicks. Those searchers have, or want, the file. The
    // Suno link import can't back a downloader promise (1,872 Suno import
    // failures logged in the 14 days to Oct 8; Suno's CDN refuses most
    // songs), so the head speaks to the file they download from Suno and
    // upload here. Brand suffix dropped to stay within 60 characters.
    title: "Suno to Video — Upload Your Suno Download (MP3, WAV, MP4)",
    description:
      "Downloaded your song from Suno as an MP3, WAV, or MP4? Upload it to Solmi and get a lip-synced music video with lyrics. Free 20-second preview, no card.",
  },
  "/ai-music-video-director": {
    title: "AI Music Video Director — Storyboard and Render | Solmi",
    description:
      "Solmi directs, storyboards, and renders music videos: it reads your song, writes a brief, builds a shot-by-shot storyboard with a consistent cast, then renders.",
  },
  "/ai-music-video-workflow": {
    title: "AI Music Video Workflow — Song to Video in One Tool | Solmi",
    description:
      "Solmi handles the full music video workflow: song analysis, creative brief, storyboard, character references, rendering, lip-sync, and export. Free to start.",
  },
  "/how-to-make-ai-music-video": {
    title: "How to Make an AI Music Video — Step-by-Step (2026)",
    description:
      "Learn how to make an AI music video from any song: upload a track, approve the storyboard, and export a lip-synced video for YouTube and TikTok. Free.",
  },
  "/ai-dance-video-generator": {
    // DirectorHome in its dance variant. ≤60 chars, brand suffix dropped
    // (Google renders the site name above the title). English here; the
    // other 13 languages come from shared/danceSeoHead.ts (see
    // getSharedComponentRouteSeo below).
    title: DANCE_SEO_HEADS.en.director.title,
    description: DANCE_SEO_HEADS.en.director.description,
  },
  "/ai-music-video-app": {
    title: "AI Music Video App for iOS & Android — Free to Start",
    description:
      "Solmi is the AI music video maker with native iOS and Android apps plus a full web app. Upload a song or Suno download to render a lip-synced music video.",
  },
};

/**
 * Look up the SEO for a path that may carry a language prefix (/es/foo).
 * `languages` is passed in by the caller so this module stays free of both the
 * server's and the client's language-list imports.
 */
export function getSharedComponentRouteSeo(
  pathname: string,
  languages: readonly string[],
): SharedComponentRouteSeo | null {
  const withoutQuery = pathname.split("?")[0]?.split("#")[0] ?? "/";
  const parts = withoutQuery.split("/").filter(Boolean);
  const hasLang = languages.includes(parts[0] ?? "");
  const basePath = hasLang
    ? `/${parts.slice(1).join("/")}`
    : `/${parts.join("/")}`;
  // The dance page is the one shared-component route with a localized head:
  // the server renders it per language (server/_core/seo.ts
  // buildDanceDirectorSeo), so hydration has to land on the same strings.
  if (hasLang && basePath === DANCE_DIRECTOR_PATH) {
    const { title, description } = getDanceSeoHead(parts[0]!).director;
    return { title, description };
  }
  // The music video generator has its own head in Japanese, Korean and
  // Portuguese (shared/mvGeneratorHead.ts; buildMvGeneratorSeo on the
  // server). The other languages keep the English entry below.
  if (hasLang && basePath === MV_GENERATOR_PATH) {
    const head = getMvGeneratorHead(parts[0]!);
    if (head) {
      return {
        title: head.title,
        description: head.description,
        keywords: head.keywords,
      };
    }
  }
  return SHARED_COMPONENT_ROUTE_SEO[basePath] ?? null;
}
